import { useCallback, useEffect, useRef, useState } from 'react'

const FALLBACK_THEAD_H = 42

function readTheadHeight(scrollEl: HTMLElement): number {
  const thead = scrollEl.querySelector('thead')
  if (!thead) return FALLBACK_THEAD_H
  const h = Math.ceil(thead.getBoundingClientRect().height)
  return h > 0 ? h : FALLBACK_THEAD_H
}

/** Đo viewport scroll + hàng dữ liệu expand — sticky row + panel fill height. */
export function useKiotListExpandViewport() {
  const tableScrollObsRef = useRef<ResizeObserver | null>(null)
  const dataRowObsRef = useRef<ResizeObserver | null>(null)
  const theadObsRef = useRef<ResizeObserver | null>(null)
  const scrollElRef = useRef<HTMLDivElement | null>(null)
  const rowElRef = useRef<HTMLTableRowElement | null>(null)

  const [scrollContainerEl, setScrollContainerEl] = useState<HTMLDivElement | null>(null)
  const [expandedDataRowEl, setExpandedDataRowEl] = useState<HTMLTableRowElement | null>(null)
  const [viewportWidthPx, setViewportWidthPx] = useState(0)
  const [viewportHeightPx, setViewportHeightPx] = useState(0)
  const [dataRowOverlapPx, setDataRowOverlapPx] = useState(0)
  const [theadHeightPx, setTheadHeightPx] = useState(FALLBACK_THEAD_H)

  const applyMetrics = useCallback(() => {
    const scrollEl = scrollElRef.current
    if (!scrollEl) return

    const w = Math.floor(scrollEl.clientWidth)
    const h = Math.floor(scrollEl.clientHeight)
    const theadH = readTheadHeight(scrollEl)
    const rowEl = rowElRef.current
    const rowH = rowEl ? Math.ceil(rowEl.getBoundingClientRect().height) : 0

    setViewportWidthPx((prev) => (prev === w ? prev : w))
    setViewportHeightPx((prev) => (prev === h ? prev : h))
    setTheadHeightPx((prev) => (prev === theadH ? prev : theadH))
    if (rowEl) {
      setDataRowOverlapPx((prev) => (prev === rowH ? prev : rowH))
    }

    const panelMaxH = rowEl ? Math.max(0, h - theadH - rowH) : 0
    scrollEl.style.setProperty('--kiot-expand-sticky-top', `${theadH}px`)
    scrollEl.style.setProperty('--kiot-expand-panel-max-h', `${panelMaxH}px`)
  }, [])

  const scrollContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      tableScrollObsRef.current?.disconnect()
      tableScrollObsRef.current = null
      theadObsRef.current?.disconnect()
      theadObsRef.current = null

      if (scrollElRef.current && scrollElRef.current !== el) {
        scrollElRef.current.style.removeProperty('--kiot-expand-sticky-top')
        scrollElRef.current.style.removeProperty('--kiot-expand-panel-max-h')
      }

      scrollElRef.current = el
      setScrollContainerEl(el)

      if (!el) {
        setViewportWidthPx(0)
        setViewportHeightPx(0)
        setTheadHeightPx(FALLBACK_THEAD_H)
        return
      }

      let raf = 0
      const schedule = () => {
        cancelAnimationFrame(raf)
        raf = requestAnimationFrame(applyMetrics)
      }
      schedule()
      const ro = new ResizeObserver(schedule)
      ro.observe(el)
      tableScrollObsRef.current = ro

      const thead = el.querySelector('thead')
      if (thead) {
        const tro = new ResizeObserver(schedule)
        tro.observe(thead)
        theadObsRef.current = tro
      }
    },
    [applyMetrics],
  )

  /** Gắn vào `TableRow` hàng dữ liệu đang expand (`data-expand-frame`). */
  const dataRowFrameRef = useCallback(
    (el: HTMLTableRowElement | null) => {
      dataRowObsRef.current?.disconnect()
      dataRowObsRef.current = null
      rowElRef.current = el
      setExpandedDataRowEl(el)

      const scrollEl = scrollElRef.current
      if (!el) {
        setDataRowOverlapPx(0)
        if (scrollEl) {
          scrollEl.style.setProperty('--kiot-expand-panel-max-h', '0px')
        }
        return
      }

      let raf = 0
      const schedule = () => {
        cancelAnimationFrame(raf)
        raf = requestAnimationFrame(() => {
          applyMetrics()
          // Chỉ pin 1 lần khi mở expand — không kéo ngược khi user chain-scroll bảng
          const sc = scrollElRef.current
          const row = rowElRef.current
          if (!sc || !row || row.dataset.kiotExpandPinned === '1') return
          const theadH = readTheadHeight(sc)
          const scrollRect = sc.getBoundingClientRect()
          const rowRect = row.getBoundingClientRect()
          const delta = rowRect.top - scrollRect.top - theadH
          if (Math.abs(delta) > 1) {
            sc.scrollTop += delta
            applyMetrics()
          }
          row.dataset.kiotExpandPinned = '1'
        })
      }
      schedule()
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf)
        raf = requestAnimationFrame(applyMetrics)
      })
      ro.observe(el)
      dataRowObsRef.current = ro
    },
    [applyMetrics],
  )

  useEffect(
    () => () => {
      tableScrollObsRef.current?.disconnect()
      dataRowObsRef.current?.disconnect()
      theadObsRef.current?.disconnect()
      const scrollEl = scrollElRef.current
      if (scrollEl) {
        scrollEl.style.removeProperty('--kiot-expand-sticky-top')
        scrollEl.style.removeProperty('--kiot-expand-panel-max-h')
      }
    },
    [],
  )

  const panelVisibleWidthPx = viewportWidthPx > 0 ? viewportWidthPx : undefined
  const rowOverlapPx = dataRowOverlapPx > 0 ? dataRowOverlapPx : undefined
  const stickyDataRowTopPx = theadHeightPx > 0 ? theadHeightPx : FALLBACK_THEAD_H
  const panelMaxHeightPx =
    expandedDataRowEl != null && viewportHeightPx > 0
      ? Math.max(0, viewportHeightPx - stickyDataRowTopPx - (dataRowOverlapPx || 0))
      : undefined

  return {
    scrollContainerRef,
    dataRowFrameRef,
    scrollContainerEl,
    expandedDataRowEl,
    panelVisibleWidthPx,
    panelMaxHeightPx,
    stickyDataRowTopPx,
    rowOverlapPx,
    viewportWidthPx,
    viewportHeightPx,
    theadHeightPx: stickyDataRowTopPx,
  }
}
